import React from 'react';
import { enums, enumValue, getNormAbnormRange } from '~/util';
import { moment, momentFormats } from '~/vendorConfigs/moment';

export function getTrimmedName(name: string) {
    return name.replace(/\s(and|or)\s*$/, '');
}

export function groupingValidationMessage(detail: { name: string }) {
    return `This field is required in conjunction with ${getTrimmedName(detail.name)}.`;
}

export function getReasonText(riskGap: RiskGapType) {
    return `${riskGap.is_utp ? '*' : ''}${riskGap?.reason ? riskGap.reason : ''}${
        riskGap?.return_comment ? ` ${riskGap.return_comment}` : ''
    }`;
}

export function getGapDetailValue(gapDetail: GapDetailType, value: number | string) {
    if (gapDetail?.lookup_metadata?.length) {
        // Matching integer value with SupplementalDataSource
        const valueInt = Number(value);
        const lookup = gapDetail.lookup_metadata.find((meta) => {
            return Number(meta.SupplementalDataValue) === valueInt;
        });

        if (lookup) {
            return lookup.SupplementalDataValueDescription;
        }
    }

    return value;
}

export function getSubmissionAnswer(
    gap?: null | GapType | RiskGapType,
    submissionAnswer?: SubmissionAnswerType
) {
    if (!gap) return '';
    const textElements: JSX.Element[] = [];
    switch (gap.type) {
        case enumValue(['note', 'type', 'Risk Gap Note']):
            if (!submissionAnswer?.answer) return '';
            return enums(['submission', 'answer', submissionAnswer.answer]);
        case enumValue(['note', 'type', 'FOC Gap Note']):
            submissionAnswer?.metadata?.responses?.forEach((response) => {
                let focGapAction: undefined | FocActionType;
                let focActionDetail: undefined | GapDetailType;

                if ('measure' in gap) {
                    gap.measure?.focs?.some((foc) =>
                        foc.foc_actions?.some((action) => {
                            focActionDetail = action.foc_action_details.find(
                                (detail) => detail.id === Number(response.foc_action_detail?.id)
                            );
                            if (typeof focActionDetail !== 'undefined') {
                                focGapAction = action;
                                return true; // Exit some()
                            }
                            return false;
                        })
                    );
                }

                if (focGapAction && focActionDetail) {
                    const focActionName = getTrimmedName(focGapAction.name);
                    const focActionDetailName = getTrimmedName(focActionDetail.name);
                    const date = response?.date
                        ? moment(response.date.slice(0, -6)).format(momentFormats.DATE)
                        : null;
                    const value = getGapDetailValue(focActionDetail, response.value);
                    let text = '';

                    if (
                        focActionName !== 'need both' &&
                        focActionName !== 'Screen' &&
                        focActionName !== focActionDetailName
                    ) {
                        text += `${focActionName}; `;
                    }
                    if (focActionDetailName) {
                        text += `${focActionDetailName}; `;
                    }
                    if (date) {
                        text += `${date}; `;
                    }
                    if (value) {
                        text += `${value};`;
                    }

                    textElements.push(
                        <span key={`${focGapAction.id}.${focActionDetail.id}`}>{text}</span>
                    );
                }
            });
            return textElements;
        case enumValue(['gap', 'type', 'Quality Gap']):
            submissionAnswer?.metadata?.responses?.forEach((response) => {
                const qualityMeasureDetailId = Number(response.quality_measure_detail?.id);
                const qualityMeasureDetail = (
                    gap as GapType
                ).measure?.quality_measure_details?.find(
                    (detail) => detail.id === qualityMeasureDetailId
                );
                if (typeof qualityMeasureDetail !== 'undefined') {
                    const date = moment(response.date.slice(0, -6)).format(momentFormats.DATE);
                    const value = getGapDetailValue(qualityMeasureDetail, response.value);
                    let text = `${qualityMeasureDetail.name}; ${date}; `;
                    if (value) {
                        text += `${value}; `;
                    }
                    textElements.push(<span key={qualityMeasureDetail.id}>{text}</span>);
                }
            });
            return textElements;
        default:
            break;
    }

    return submissionAnswer?.answer;
}

export function outOfRangeText(
    detail: QualityMeasureDetailType,
    isAbnormalText: boolean,
    resultName?: string,
    whichRatio?: string
) {
    const { normRange, abnormRange } = getNormAbnormRange(detail, whichRatio);
    return (
        <>
            <span>{`${resultName || 'Result'} ${
                isAbnormalText
                    ? 'is in the abnormal range, are you sure this is correct?'
                    : 'must be within ranges:'
            }`}</span>
            <br />
            Normal: {normRange}
            <br />
            Abnormal: {abnormRange}
        </>
    );
}

export function getFocActionName(action: FocActionType) {
    if (action.name === 'need both') {
        return action.foc_action_details.map((x) => x.name).join(' ');
    }
    if (action.name === 'Screen') {
        return action.foc_action_details[0]?.name;
    }
    return action.name;
}

export function formatTitleText(text: string | { props?: { children: React.ReactNode } }[]) {
    if (!Array.isArray(text)) return text;
    return text
        .map((x) => {
            if (x?.props?.children) {
                return x.props.children;
            }
            return '';
        })
        .join('\n');
}
